import React, { useContext, useMemo, useState } from "react";
import clsx from "clsx";
import { HvGrid } from "@hitachivantara/uikit-react-core";
import { useTheme } from "@material-ui/core";

import { useTranslation } from "react-i18next";
import { FieldArray, Formik, FormikErrors, FormikHelpers } from "formik";
import * as yup from "yup";

import { useSnackbar } from "lib/provider/SnackbarProvider";
import {
  addIncidentType,
  Attribute,
  IncidentType,
  IncidentTypeFormData,
} from "lib/api";

import usePreventNavigation from "lib/hooks/usePreventNavigation";
import { useHistory } from "react-router-dom";
import AddIncidentTypeForm from "components/incidentType/IncidentTypeForm";
import IncidentTypeMapMarker from "components/incidentType/IncidentTypeMapMarker";
import IncidentTypeOptions from "components/incidentType/IncidentTypeOptions";
import IncidentTypeProperties from "components/incidentType/IncidentTypeProperties";
import IncidentTypeSop from "components/incidentType/IncidentTypeSop";
import Loading from "components/layout/Loading";
import Container from "components/layout/Container";
import Banner from "components/common/Banner";
import { FormHeader } from "components/form";

import { ModuleNames, ModulePolicyTypes } from "lib/api/userSettings";
import { UserDataContext } from "lib/context/UserDataContext";
import NotFound from "components/common/NotFound";
import useStyles from "./styles";

interface AddIncidentTypeStatus {
  serverSideError?: string;
  serverSideErrors?: FormikErrors<Partial<IncidentTypeFormData>>;
}

const AddIncidentType: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const INCIDENT_TYPE = "/settings/incident-type";

  const initialValues: Partial<IncidentTypeFormData> = {
    inc_type_name: "",
    inc_type_desc: "",
    fk_cat_id: undefined,
    fk_sub_cat_id: undefined,
    appearance: {
      color: theme.hv.palette.semantic.sema4,
      icon_type: "none",
      icon: null,
      icon_attachment: null,
    },
    has_advanced_insights: false,
    refresh_intervals: 0,
    has_timeline: false,
    attributes: [],
    inc_type_sop_map: [],
  };

  // when getting initial values from the server on edit,
  // Attributes need to be converted to the AttributesFormData
  // Basically the opposite of the save operation

  const initialStatus: AddIncidentTypeStatus = { serverSideErrors: {} };
  const { t } = useTranslation("addEditIncidentType");
  const history = useHistory();
  const [showBanner, setShowBanner] = useState(false);
  const [, setPreventNavigation] = usePreventNavigation();

  const sections = [
    { label: t("sections.details.title"), value: "details" },
    {
      label: t("sections.mapMarker.title"),
      value: "mapMarker",
    },
    {
      label: t("sections.options.title"),
      value: "options",
    },
    {
      label: t("sections.properties.title"),
      value: "properties",
    },
    { label: t("sections.sop.title"), value: "sop" },
  ];

  const onSave = (data: Partial<IncidentType>) => {
    return addIncidentType({
      inc_type_name: data.inc_type_name?.trim(),
      fk_cat_id: data.fk_cat_id,
      fk_sub_cat_id: data.fk_sub_cat_id,
      inc_type_desc: data?.inc_type_desc?.trim(),
      appearance: data?.appearance,
      refresh_intervals: data?.refresh_intervals,
      has_timeline: data?.has_timeline,
      has_advanced_insights: data?.has_advanced_insights,
      attributes: data?.attributes,
      inc_type_sop_map: data?.inc_type_sop_map,
    });
  };

  const validationSchema = useMemo(() => {
    return yup.object({
      inc_type_name: yup.string().trim().required(t("validation.nameRequired")),
      fk_cat_id: yup.string().required(t("validation.categoryRequired")),
      fk_sub_cat_id: yup.string().required(t("validation.subcategoryRequired")),
      description: yup.string().trim(),
      appearance: yup.object({
        color: yup.string().required(t("validation.colorRequired")),
        icon_type: yup
          .string()
          .oneOf(["none", "url", "fontawesome", "attachment_id"])
          .required(t("validation.iconTypeRequired")),
        icon: yup
          .string()
          .nullable()
          .when("icon_type", {
            is: (iconType: string) => iconType !== "none",
            then: yup
              .string()
              .nullable()
              .required(t("validation.iconRequired")),
          })
          .when("icon_type", {
            is: (iconType: string) => iconType === "url",
            then: yup
              .string()
              .nullable()
              .required(t("validation.iconRequired"))
              .url(t("validation.iconUrlNotValid"))
              .test(
                "Check starts with",
                t("validation.iconUrlProtocolNotValid", {
                  protocol: window.location.protocol,
                }),
                (value) => {
                  return value
                    ? value.startsWith(window.location.protocol)
                    : false;
                }
              ),
          }),
        icon_attachment: yup
          .string()
          .nullable()
          .when("icon_type", {
            is: "attachment_id",
            then: yup
              .string()
              .nullable()
              .required(t("validation.iconAttachmentIdRequired")),
          }),
      }),
      refresh_intervals: yup
        .number()
        .required(t("validationRefreshIntervalRequired")),
      has_timeline: yup.boolean().required(t("validationTimelineRequired")),
      has_advanced_insights: yup
        .boolean()
        .required(t("advancedInsightsRequired")),
    });
  }, [t]);

  const onSubmitHandler = async (
    data: Partial<IncidentTypeFormData>,
    actions: FormikHelpers<Partial<IncidentTypeFormData>>
  ) => {
    try {
      const attrs: Record<string, Attribute> = {};
      data.attributes?.forEach(
        ({ id, label, type, visibility, ...others }, index) => {
          attrs[id as string] = {
            ...others,
            order: index,
            type: type as string,
            label: (label as string).trim(),
            appearance: {
              visible: visibility !== "hidden",
              show_in_detail_page: visibility !== "hidden",
              show_in_preview_panel: visibility === "always",
            },
          };
        }
      );

      await onSave({
        ...data,
        attributes: attrs,
      });
      enqueueSnackbar(t("validation.success.add"), { variant: "success" });
      setPreventNavigation(false);
      history.push(INCIDENT_TYPE);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const errorCode = error?.response?.data?.error?.code;
      if (errorCode?.endsWith("_name_already_taken")) {
        actions.setStatus({
          serverSideErrors: {
            inc_type_name: t("validation.nameAlreadyTaken"),
          },
        });
        return;
      }
      if (errorCode === "hvs_external_communication_error") {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const hvsErrorCode = JSON.parse(error?.response?.data?.error?.message)
          .error?.code;
        if (hvsErrorCode?.endsWith("_name_already_taken")) {
          actions.setStatus({
            serverSideErrors: {
              inc_type_name: t("validation.nameAlreadyTaken"),
            },
          });
          return;
        }
      }

      setShowBanner(true);
    }
  };

  return (
    <Formik
      initialStatus={initialStatus}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmitHandler}
    >
      {(props) => {
        const { handleSubmit, isSubmitting } = props;

        const goBackHandler = () => {
          history.goBack();
        };

        const onBannerCloseHandler = () => {
          setShowBanner(false);
        };

        return (
          <Container className={classes.container} maxWidth="xl">
            <Banner
              open={showBanner}
              variant="error"
              showIcon
              label={t("validation.unknownError.add")}
              onClose={onBannerCloseHandler}
            />
            <form onSubmit={handleSubmit} noValidate>
              <FormHeader
                title={t("globalAction.title")}
                sections={sections}
                onCancel={goBackHandler}
              />
              <HvGrid container spacing={0}>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[0].value}
                  xs={12}
                  item
                >
                  <AddIncidentTypeForm />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[1].value}
                  xs={12}
                  item
                >
                  <IncidentTypeMapMarker />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[2].value}
                  xs={12}
                  item
                >
                  <IncidentTypeOptions />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[3].value}
                  xs={12}
                  item
                >
                  <FieldArray
                    name="attributes"
                    render={(arrayHelpers) => (
                      <IncidentTypeProperties {...arrayHelpers} />
                    )}
                  />
                </HvGrid>
                <HvGrid
                  className={classes.sectionMargin}
                  tabIndex={-1}
                  id={sections[4].value}
                  xs={12}
                  item
                >
                  <IncidentTypeSop />
                </HvGrid>
              </HvGrid>
            </form>
            {isSubmitting && (
              <Loading
                delay={500}
                loadingLabel={t("submitting")}
                className={clsx(classes.loading, classes.zIndex)}
              />
            )}
          </Container>
        );
      }}
    </Formik>
  );
};
export const IncidentTypeAddAuth: React.FC = () => {
  const { hasModulePolicy } = useContext(UserDataContext);
  const hasRight = useMemo(() => {
    return hasModulePolicy(
      ModuleNames.incidentManagement,
      ModulePolicyTypes.incident
    );
  }, [hasModulePolicy]);
  return (
    <>
      {hasRight ? (
        <Container noHVContainer>
          <AddIncidentType />
        </Container>
      ) : (
        <NotFound />
      )}
    </>
  );
};
export default AddIncidentType;
